import React, { useCallback, useEffect, useMemo, useState } from "react";
import { HashLink } from "react-router-hash-link";
import { ethers } from "ethers";
import { useAccount, useConnect, useSignMessage } from "wagmi";
import { useAuth } from "../../context/AuthContext";
import { isTrdxSaleEnvExplicitlyConfigured } from "../../services/web3/trdxSaleEnv";
import { readTrdxBalance } from "../../services/web3/trdxSaleService";
import AddTrdxToMetaMaskButton from "../treydorex/AddTrdxToMetaMaskButton";

/**
 * Buy flow: public site uses Home `#sales` for Treydorex today.
 */
const BUY_TRDX_HASH_PATH = "/#sales";
const TRDX_DECIMALS = 18;

function compactAddr(addr) {
  if (!addr || typeof addr !== "string") return "";
  const a = addr.trim();
  if (a.length < 10) return a;
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}

function getPrimaryEthereumWallet(user) {
  const list = Array.isArray(user?.wallets) ? user.wallets : [];
  const eth = list.filter(
    (w) =>
      w &&
      typeof w === "object" &&
      (!w.chain || w.chain === "ethereum") &&
      typeof w.address === "string" &&
      w.address.trim() !== ""
  );
  const primary = eth.find((w) => w.isPrimary);
  return primary || eth[0] || null;
}

function formatTrdxBalanceHuman(balanceWei) {
  const n = Number(ethers.utils.formatUnits(balanceWei, TRDX_DECIMALS));
  if (!Number.isFinite(n)) return "—";
  return `${n.toLocaleString("en-US", { maximumFractionDigits: 4 })} TRDX`;
}

const WalletAccountPanel = () => {
  const { user, refreshFromStorage, linkWallet } = useAuth();
  const { address, isConnected } = useAccount();
  const { connectAsync, connectors } = useConnect();
  const { signMessageAsync } = useSignMessage();

  const [busy, setBusy] = useState(false);
  const [okMsg, setOkMsg] = useState("");
  const [errMsg, setErrMsg] = useState("");
  const [balanceState, setBalanceState] = useState("idle");

  const linked = useMemo(() => getPrimaryEthereumWallet(user), [user]);
  const linkedLine = linked?.address ? compactAddr(linked.address) : "Not connected";

  const balanceWalletAddress = useMemo(() => {
    const linkedAddr = String(linked?.address || "").trim();
    if (linkedAddr) return linkedAddr;
    if (isConnected && address) return address;
    return "";
  }, [linked, isConnected, address]);

  const trdxEnvConfigured = isTrdxSaleEnvExplicitlyConfigured();

  useEffect(() => {
    let cancelled = false;

    if (!balanceWalletAddress) {
      setBalanceState("no_wallet");
      return undefined;
    }
    if (!trdxEnvConfigured) {
      setBalanceState("config_missing");
      return undefined;
    }

    setBalanceState("loading");
    readTrdxBalance(balanceWalletAddress)
      .then((wei) => {
        if (cancelled) return;
        setBalanceState({ kind: "ok", wei });
      })
      .catch(() => {
        if (cancelled) return;
        setBalanceState("unavailable");
      });

    return () => {
      cancelled = true;
    };
  }, [balanceWalletAddress, trdxEnvConfigured]);

  const balanceLine = useMemo(() => {
    if (!balanceWalletAddress) return "Connect or link a wallet to view balance.";
    if (!trdxEnvConfigured) return "TRDX config missing on this build.";
    if (balanceState === "loading") return "Loading on-chain balance…";
    if (balanceState === "unavailable") return "Balance unavailable";
    if (balanceState?.kind === "ok") return formatTrdxBalanceHuman(balanceState.wei);
    return "Balance unavailable";
  }, [balanceWalletAddress, trdxEnvConfigured, balanceState]);

  const balanceIsMuted =
    !balanceWalletAddress ||
    !trdxEnvConfigured ||
    balanceState === "loading" ||
    balanceState === "unavailable";

  const accountTypeLabel = useMemo(() => {
    const providers = Array.isArray(user?.authProviders) ? user.authProviders : [];
    const walletOnly = !user?.email && providers.includes("wallet");
    if (walletOnly) return "Wallet Account";
    return "Treydora Account";
  }, [user]);

  const browserConnected = Boolean(isConnected && address);
  const showLinkThisWallet = Boolean(browserConnected && !linked);
  const showLinkWallet = Boolean(!browserConnected && !linked);

  const accountStatusLabel = useMemo(() => {
    if (String(user?.email || "").trim()) return "Email on file";
    return "Active session";
  }, [user]);

  const runLinkFlow = useCallback(async () => {
    setBusy(true);
    setOkMsg("");
    setErrMsg("");
    try {
      let activeAddress = address;
      if (!isConnected || !activeAddress) {
        const connList = Array.isArray(connectors) ? connectors : [];
        const connector = connList.find((c) => c && c.ready) || connList[0];
        if (!connector) {
          throw new Error("No wallet connector available. Install a browser wallet or enable WalletConnect.");
        }
        const connected = await connectAsync({ connector });
        activeAddress = connected?.account;
      }
      if (!activeAddress) {
        throw new Error("Wallet not connected.");
      }

      setOkMsg(
        "Sign a message to link your wallet. This does not cost gas and does not give permission to move funds."
      );
      await linkWallet({ walletAddress: activeAddress, signMessageAsync });
      refreshFromStorage();
      setOkMsg("Wallet linked to your Treydora account.");
    } catch (e) {
      const msg =
        e?.shortMessage ||
        e?.message ||
        (typeof e === "string" ? e : "Unable to complete wallet link.");
      if (/rejected|denied|user rejected/i.test(String(msg))) {
        setErrMsg("Signature was rejected in the wallet.");
      } else if (/not connected|connect/i.test(String(msg)) && !isConnected) {
        setErrMsg("Connect a wallet first, then try again.");
      } else {
        setErrMsg(String(msg));
      }
    } finally {
      setBusy(false);
    }
  }, [
    address,
    connectAsync,
    connectors,
    isConnected,
    linkWallet,
    refreshFromStorage,
    signMessageAsync,
  ]);

  return (
    <section className="dg-panel dg-panel--compact dg-gold-ring dg-premium-ring dg-wallet dg-wallet--trdx">
      <h2 className="dg-panel__title serif">Wallet &amp; Account</h2>

      <div className="dg-wallet-trdx">
        <div className="dg-wallet-trdx__emblem-stack">
          <span className="dg-wallet-trdx__orbit" aria-hidden />
          <span className="dg-wallet-trdx__orbit dg-wallet-trdx__orbit--delayed" aria-hidden />
          <div className="dg-wallet-trdx__emblem" aria-hidden="true" title="Treydorex">
            <span className="dg-wallet-trdx__emblem-inner">T</span>
          </div>
        </div>
        <div className="dg-wallet-trdx__main">
          <span className="dg-label-up">Treydorex Balance</span>
          <div
            className={`dg-wallet-trdx__balance serif${balanceIsMuted ? " dg-wallet-trdx__balance--muted" : ""}`}
          >
            {balanceLine}
          </div>
          <p className="dg-wallet-trdx__sub">
            {trdxEnvConfigured
              ? "On-chain TRDX for your linked or connected wallet. Purchases use the public wallet sale when available."
              : "Set REACT_APP_TRDX_* on this build to show live balances. Dashboard access does not require TRDX."}
          </p>
          {trdxEnvConfigured && balanceWalletAddress ? (
            <AddTrdxToMetaMaskButton variant="dashboard" />
          ) : null}
        </div>
      </div>

      <div className="dg-divider-gold" role="presentation" />

      <ul className="dg-wallet-trdx__meta">
        <li>
          <span className="dg-label-up">Account Type</span>
          <span className="dg-wallet-trdx__mono">{accountTypeLabel}</span>
        </li>
        <li>
          <span className="dg-label-up">Account Status</span>
          <span className="dg-pill dg-pill--vault-verified">
            <i className="fas fa-check-circle" aria-hidden /> {accountStatusLabel}
          </span>
        </li>
        <li className="dg-wallet-trdx__wallet-row">
          <span className="dg-label-up">Connected wallet</span>
          <span className="dg-wallet-trdx__mono">{linkedLine}</span>
        </li>
      </ul>

      {okMsg ? (
        <p className="dg-wallet-trdx__link-msg dg-wallet-trdx__link-msg--ok" role="status">
          {okMsg}
        </p>
      ) : null}
      {errMsg ? (
        <p className="dg-wallet-trdx__link-msg dg-wallet-trdx__link-msg--err" role="alert">
          {errMsg}
        </p>
      ) : null}

      <div className="dg-wallet-trdx__actions">
        <HashLink smooth to={BUY_TRDX_HASH_PATH} className="dg-btn dg-btn--trdx-primary dg-btn--sweep">
          Buy More TRDX <i className="fas fa-arrow-right dg-btn__icon" aria-hidden />
        </HashLink>
        {showLinkWallet && (
          <button
            type="button"
            className="dg-btn dg-btn--trdx-secondary"
            disabled={busy}
            onClick={() => runLinkFlow()}
          >
            Link Wallet <i className="fas fa-link dg-btn__icon" aria-hidden />
          </button>
        )}
        {showLinkThisWallet && (
          <button
            type="button"
            className="dg-btn dg-btn--trdx-secondary"
            disabled={busy}
            onClick={() => runLinkFlow()}
          >
            Link This Wallet <i className="fas fa-link dg-btn__icon" aria-hidden />
          </button>
        )}
      </div>

      <p className="dg-legal-micro dg-legal-micro--tight">
        Read-only on-chain balance when configured. This panel does not custody funds or run card checkout for TRDX.
      </p>
    </section>
  );
};

export default WalletAccountPanel;
